import { errors } from '../error-handling/errors'
import { trackEvent } from '../events/trackEvent'
import { AUTH_STAGES, ERROR_TYPES } from './authConstants'
import { createPrivacySafeUserContext, obfuscateEmailsInText } from './emailObfuscation'

interface UserContext {
  email: string | null
  userId: string
  emailError?: string
  userAgent?: string
  timestamp?: string
  url?: string
}

/**
 * Extract user context from JWT token payload
 */
export const extractUserContextFromJWT = (token: string): UserContext => {
  try {
    const jwtParts = token.split('.')
    if (jwtParts.length !== 3) {
      return {
        email: null,
        emailError: 'jwt_invalid_format',
        userId: 'unknown',
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      }
    }

    const tokenPayload = JSON.parse(atob(jwtParts[1]))

    return {
      email: tokenPayload.email || tokenPayload.sub || tokenPayload.rpp_uuid || null,
      emailError:
        tokenPayload.email || tokenPayload.sub || tokenPayload.rpp_uuid
          ? undefined
          : 'no_email_claim',
      userId:
        tokenPayload.user_id ||
        tokenPayload.id ||
        tokenPayload.sub ||
        tokenPayload.rpp_uuid ||
        'unknown_no_user_id',
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    }
  } catch (error) {
    return {
      email: null,
      emailError: 'jwt_parse_failed',
      userId: 'unknown',
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    }
  }
}

/**
 * Extract user context from auth flow state (localStorage, URL params, etc.)
 */
export const extractUserContextFromAuthFlow = (): UserContext => {
  try {
    // Try to get email from various sources during auth flow
    const storedEmail = localStorage.getItem('auth_email')
    const urlParams = new URLSearchParams(window.location.search)
    const emailFromUrl = urlParams.get('login_hint') || urlParams.get('email')

    // Try to get stored user info from PKCE state if available
    const pkceState = localStorage.getItem('pkce_state')
    let emailFromState = null
    if (pkceState) {
      try {
        // If we store user info in PKCE state (base64 encoded)
        const stateData = JSON.parse(atob(pkceState))
        emailFromState = stateData.email
      } catch {
        // PKCE state might not contain user info, that's ok
      }
    }

    const email = storedEmail || emailFromUrl || emailFromState || null

    return {
      email: email,
      emailError: email ? undefined : 'no_auth_context',
      userId: 'unknown_auth_flow',
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    }
  } catch (error) {
    return {
      email: null,
      emailError: 'context_extraction_failed',
      userId: 'unknown',
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    }
  }
}

/**
 * Creates a user context specifically for preflight check errors
 * Since preflight happens before full auth flow, we have limited context
 */
export const extractUserContextFromPreflight = (email?: string): UserContext => {
  const timestamp = new Date().toISOString()
  const userAgent = navigator.userAgent
  const url = window.location.href

  return {
    email: email || null,
    emailError: email ? undefined : 'preflight_email_missing',
    userId: 'unknown_preflight_user',
    timestamp,
    url,
    userAgent,
  }
}

/**
 * Sanitizes additional context to remove any raw emails
 */
const sanitizeAdditionalContext = (
  additionalContext?: Record<string, any>
): Record<string, any> => {
  if (!additionalContext) return {}

  const sanitized: Record<string, any> = {}
  for (const [key, value] of Object.entries(additionalContext)) {
    if (typeof value === 'string') {
      sanitized[key] = obfuscateEmailsInText(value)
    } else {
      // For non-string values, keep as-is (numbers, booleans, etc.)
      sanitized[key] = value
    }
  }
  return sanitized
}

export const emitAuthMetrics = (
  errorType: string,
  authStage: string,
  failureReason: string,
  userContext: UserContext,
  additionalContext?: Record<string, any>
) => {
  try {
    const privacySafeContext = createPrivacySafeUserContext(userContext)
    const sanitizedAdditionalContext = sanitizeAdditionalContext(additionalContext)
    trackEvent({
      id: 'ipp.auth.error_occurred',
      description: 'IPP Authentication error occurred',
      data: {
        errorType,
        authStage,
        failureReason,
        hasEmail: Boolean(privacySafeContext.email),
        emailError: privacySafeContext.emailError,
        userAgent: privacySafeContext.userAgent,
        ...sanitizedAdditionalContext,
      },
    })
  } catch (error) {
    console.warn('Failed to emit auth metrics:', error)
  }
}

export const emitAuthStageSuccessMetrics = (
  authStage: string,
  userContext: UserContext,
  additionalContext?: Record<string, any>
) => {
  try {
    const privacySafeContext = createPrivacySafeUserContext(userContext)
    const sanitizedAdditionalContext = sanitizeAdditionalContext(additionalContext)
    trackEvent({
      id: 'ipp.auth.stage_success',
      description: 'IPP Authentication stage completed successfully',
      data: {
        authStage,
        hasEmail: Boolean(privacySafeContext.email),
        userAgent: privacySafeContext.userAgent,
        timestamp: new Date().toISOString(),
        ...sanitizedAdditionalContext,
      },
    })
  } catch (error) {
    console.warn('Failed to emit auth stage success metrics:', error)
  }
}

export const emitAuthFlowSuccessMetrics = (
  userContext: UserContext,
  additionalContext?: Record<string, any>
) => {
  try {
    const privacySafeContext = createPrivacySafeUserContext(userContext)
    const sanitizedAdditionalContext = sanitizeAdditionalContext(additionalContext)
    trackEvent({
      id: 'ipp.auth.flow_success',
      description: 'IPP Authentication flow completed successfully',
      data: {
        authStage: 'authentication_flow_complete',
        hasEmail: Boolean(privacySafeContext.email),
        userAgent: privacySafeContext.userAgent,
        timestamp: new Date().toISOString(),
        ...sanitizedAdditionalContext,
      },
    })
  } catch (error) {
    console.warn('Failed to emit auth flow success metrics:', error)
  }
}

export const emitSessionLifecycleMetrics = (
  eventType: string,
  userContext: UserContext,
  additionalContext?: Record<string, any>
) => {
  try {
    const privacySafeContext = createPrivacySafeUserContext(userContext)
    const sanitizedAdditionalContext = sanitizeAdditionalContext(additionalContext)
    trackEvent({
      id: 'ipp.session.lifecycle',
      description: 'IPP Session lifecycle event',
      data: {
        eventType,
        hasEmail: Boolean(privacySafeContext.email),
        userAgent: privacySafeContext.userAgent,
        timestamp: new Date().toISOString(),
        ...sanitizedAdditionalContext,
      },
    })
  } catch (error) {
    console.warn('Failed to emit session lifecycle metrics:', error)
  }
}

export const logAuthenticationError = (
  error: Error,
  errorType: string,
  authStage: string,
  failureReason: string,
  userContext: UserContext,
  additionalContext?: Record<string, any>
) => {
  // Extract detailed error information automatically
  const errorDetails = {
    name: error instanceof Error ? error.name : 'Unknown',
    message: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? error.stack : undefined,
    cause: error instanceof Error ? error.cause : undefined,
  }

  // Merge error details with additional context
  const enhancedContext = {
    ...additionalContext,
    errorDetails,
  }

  const privacySafeContext = createPrivacySafeUserContext(userContext)
  const sanitizedAdditionalContext = sanitizeAdditionalContext(enhancedContext)

  // Emit metrics (handles privacy internally)
  emitAuthMetrics(errorType, authStage, failureReason, userContext, {
    ...sanitizedAdditionalContext,
  })

  // Capture exception with privacy-safe context
  try {
    errors.captureException(error, `Authentication error in ${authStage}`, {
      level: 'error',
      logToConsole: true,
      ...createAuthErrorContext(errorType, authStage, failureReason, privacySafeContext, {
        ...sanitizedAdditionalContext,
      }),
    })
  } catch (captureError) {
    console.warn('Failed to capture authentication error:', captureError)
  }
}

/**
 * Logs preflight check errors with structured context for monitoring
 * Uses obfuscated email for privacy protection
 */
export const logPreflightError = (
  error: Error,
  email: string,
  failureReason: string,
  additionalContext?: Record<string, any>
) => {
  const userContext = extractUserContextFromPreflight(email)
  logAuthenticationError(
    error,
    ERROR_TYPES.PREFLIGHT_FAILURE,
    AUTH_STAGES.PREFLIGHT_CHECK,
    failureReason,
    userContext,
    additionalContext
  )
}

/**
 * Enhanced error context for auth-related errors
 */
export const createAuthErrorContext = (
  errorType: string,
  authStage: string,
  failureReason: string,
  userContext: UserContext,
  additionalContext?: Record<string, any>
) => ({
  extra: {
    errorType,
    authStage,
    failureReason,
    ...userContext,
    ...additionalContext,
  },
})
